import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Button,
  TextField,
  Grid,
  IconButton,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Pagination,
  Stack,
  Alert,
  Chip,
  Skeleton,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { getData, postData, putData, deleteData } from "../../Webservice/Webservice";
import { MdDeleteForever, MdEdit } from "react-icons/md";
import { IoPersonAddSharp } from "react-icons/io5";
import { FiFilter as FilterIcon } from "react-icons/fi";
import { Drawer } from "@mui/material";
import { indianStates } from "../../Utils/states";

// Reusable sx object for Raleway font
const MulishFont = {
  fontFamily: "Mulish, sans-serif",
};

function SupplierDetails() {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    _id: null,
    name: "",
    contactPerson: "",
    phone: "",
    email: "",
    businessType: "",
    panNumber: "",
    gstNumber: "",
    tradeLicense: "",
    billingAddress: {
      street: "",
      city: "",
      state: "",
      country: "",
      pincode: "",
    },
    bankDetails: {
      accountHolder: "",
      accountNumber: "",
      ifsc: "",
      swift: "",
      upiId: "",
      preferredMode: "",
    },
    products: [],
    status: "active",
  });
  const [productsOptions, setProductsOptions] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [suppliersData, setSuppliersData] = useState([]);
  const [loading, setLoading] = useState(true); // Consolidated loading state
  const [error, setError] = useState(""); // Added error state
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [supplierToDelete, setSupplierToDelete] = useState(null);

  // Validation state
  const [errors, setErrors] = useState({});

  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });

  // Filter state
  const [filters, setFilters] = useState({
    businessType: "",
    panNumber: "",
    gstNumber: "",
    status: "",
    city: "",
    state: "",
    phone: "",
    contactPerson: ""
  });

  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [tempFilters, setTempFilters] = useState({
    businessType: "",
    panNumber: "",
    gstNumber: "",
    status: "",
    city: "",
    state: "",
    phone: "",
    contactPerson: ""
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      setError("");
      const tokenValue = sessionStorage.getItem("token");
      const token = tokenValue ? `Bearer ${tokenValue}` : "";

      // Fetch products if form is open
      if (showForm) {
        const productsData = await getData("product/list?limit=all", null, token);
        setProductsOptions(Array.isArray(productsData?.products) ? productsData.products : []);
      }

      // Fetch suppliers if form is not open
      if (!showForm) {
        const params = {
          page: pagination.page,
          limit: pagination.limit,
          ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== ""))
        };
        const suppliersDataResponse = await getData(
          "supplier/list",
          params,
          token,
          false
        );
        setSuppliersData(Array.isArray(suppliersDataResponse?.suppliers) ? suppliersDataResponse.suppliers : []);
        if (suppliersDataResponse?.meta) {
          setPagination((prev) => ({
            ...prev,
            total: suppliersDataResponse.meta.total || 0,
            totalPages: suppliersDataResponse.meta.totalPages || 0,
          }));
        }
      }
    } catch (error) {
      setError("Failed to fetch data. Please try again later.");
      console.error("Failed to fetch data:", error);
      if (showForm) setProductsOptions([]);
      else setSuppliersData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [showForm, pagination.page, pagination.limit, filters]);

  // Pagination handler
  const handlePageChange = (event, page) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  const handleToggleView = () => setShowForm(!showForm);

  const handleCloseForm = () => {
    setShowForm(false);
    setFormData({
      _id: null,
      name: "",
      contactPerson: "",
      phone: "",
      email: "",
      businessType: "",
      panNumber: "",
      gstNumber: "",
      tradeLicense: "",
      billingAddress: {
        street: "",
        city: "",
        state: "",
        country: "",
        pincode: "",
      },
      bankDetails: {
        accountHolder: "",
        accountNumber: "",
        ifsc: "",
        swift: "",
        upiId: "",
        preferredMode: "",
      },
      products: [],
      status: "active",
    });
    setSelectedProducts([]);
    setErrors({});
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let finalValue = value;
    if (name === "panNumber" || name === "gstNumber") {
      finalValue = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    }
    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setFormData((prevState) => ({
        ...prevState,
        [parent]: {
          ...prevState[parent],
          [child]: finalValue,
        },
      }));
    } else {
      setFormData((prevState) => ({
        ...prevState,
        ...prevState,
        [name]: finalValue,
      }));
    }
  };

  const handleRowClick = (supplier) => {
    setFormData({
      _id: supplier._id,
      name: supplier.name || "",
      contactPerson: supplier.contactPerson || "",
      phone: supplier.phone || "",
      email: supplier.email || "",
      businessType: supplier.businessType || "",
      panNumber: supplier.panNumber || "",
      gstNumber: supplier.gstNumber || "",
      tradeLicense: supplier.tradeLicense || "",
      billingAddress: {
        street: supplier.billingAddress?.street || "",
        city: supplier.billingAddress?.city || "",
        state: supplier.billingAddress?.state || "",
        country: supplier.billingAddress?.country || "",
        pincode: supplier.billingAddress?.pincode || "",
      },
      bankDetails: {
        accountHolder: supplier.bankDetails?.accountHolder || "",
        accountNumber: supplier.bankDetails?.accountNumber || "",
        ifsc: supplier.bankDetails?.ifsc || "",
        swift: supplier.bankDetails?.swift || "",
        upiId: supplier.bankDetails?.upiId || "",
        preferredMode: supplier.bankDetails?.preferredMode || "",
      },
      products: (supplier.products || []).map(p => ({
        productId: p.productId?._id || p.productId,
        moq: p.moq || 0,
        leadTime: p.leadTime || 0
      })),
      status: supplier.status || "active",
    });

    const mappedSelected = (supplier.products || []).map(p => {
      const prodObj = p.productId && typeof p.productId === 'object' ? p.productId : { _id: p.productId };
      return {
        ...prodObj,
        moq: p.moq || 0,
        leadTime: p.leadTime || 0
      };
    });
    setSelectedProducts(mappedSelected);
    setShowForm(true);
    setErrors({});
  };

  const handleDeleteClick = (supplierId, e) => {
    e.stopPropagation();
    setSupplierToDelete(supplierId);
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const tokenValue = sessionStorage.getItem("token");
      const token = tokenValue ? `Bearer ${tokenValue}` : "";
      await deleteData(`supplier/delete/${supplierToDelete}`, token);
      await fetchData(); // Refresh data after deletion
      setOpenDeleteDialog(false);
      setSupplierToDelete(null);
    } catch (err) {
      console.error("Failed to delete supplier:", err);
      setOpenDeleteDialog(false);
      setSupplierToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setOpenDeleteDialog(false);
    setSupplierToDelete(null);
  };

  // Allowed values for preferredMode enum (server expects one of these)
  const ALLOWED_PREFERRED_MODES = ["Bank Transfer", "UPI", "Cheque", "Cash"];

  // Regex helpers
  const UPI_REGEX = /^[\w.\-]{2,}@[a-zA-Z]{3,}$/; // basic username@bank (adjust if you need stricter)
  const SWIFT_REGEX = /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/; // 8 or 11 chars

  // Validation function
  const validateForm = () => {
    const newErrors = {};

    // Name & Contact
    if (!formData.name || !formData.name.toString().trim()) {
      newErrors.name = "Business name is required";
    }
    if (!formData.contactPerson || !formData.contactPerson.toString().trim()) {
      newErrors.contactPerson = "Contact person is required";
    }

    // Phone - 10 digits (normalize)
    const rawPhone = (formData.phone || "").toString();
    const cleanedPhone = rawPhone.replace(/\D/g, "");
    if (!cleanedPhone) {
      newErrors.phone = "Phone is required";
    } else if (!/^\d{10}$/.test(cleanedPhone)) {
      newErrors.phone = "Enter a valid 10-digit phone number";
    }

    // Email
    if (!formData.email || !formData.email.toString().trim()) {
      newErrors.email = "Email is required";
    } else if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }

    // Business Type
    if (!formData.businessType) {
      newErrors.businessType = "Business type is required";
    }

    // PAN — required per original UI
    if (!formData.panNumber || !formData.panNumber.toString().trim()) {
      newErrors.panNumber = "PAN Number is required";
    }

    // Billing address
    if (!formData.billingAddress.street || !formData.billingAddress.street.toString().trim()) {
      newErrors["billingAddress.street"] = "Street address is required";
    }
    if (!formData.billingAddress.city || !formData.billingAddress.city.toString().trim()) {
      newErrors["billingAddress.city"] = "City is required";
    }
    if (!formData.billingAddress.state || !formData.billingAddress.state.toString().trim()) {
      newErrors["billingAddress.state"] = "State is required";
    }
    if (!formData.billingAddress.pincode || !formData.billingAddress.pincode.toString().trim()) {
      newErrors["billingAddress.pincode"] = "Pincode is required";
    } else {
      const p = formData.billingAddress.pincode.toString().trim();
      if (!/^\d{4,6}$/.test(p)) {
        // allow 4-6 digits common for pincodes; adjust if you want strict 6
        newErrors["billingAddress.pincode"] = "Enter a valid pincode";
      }
    }
    if (!formData.billingAddress.country || !formData.billingAddress.country.toString().trim()) {
      newErrors["billingAddress.country"] = "Country is required";
    }

    // Bank details
    if (!formData.bankDetails.accountHolder || !formData.bankDetails.accountHolder.toString().trim()) {
      newErrors["bankDetails.accountHolder"] = "Account holder name is required";
    }
    if (!formData.bankDetails.accountNumber || !formData.bankDetails.accountNumber.toString().trim()) {
      newErrors["bankDetails.accountNumber"] = "Account number is required";
    } else {
      // basic numeric check (allowing spaces/dashes)
      const acct = formData.bankDetails.accountNumber.toString().replace(/\s|-/g, "");
      if (!/^\d{6,24}$/.test(acct)) {
        newErrors["bankDetails.accountNumber"] = "Enter a valid account number (6-24 digits)";
      }
    }
    if (!formData.bankDetails.ifsc || !formData.bankDetails.ifsc.toString().trim()) {
      newErrors["bankDetails.ifsc"] = "IFSC code is required";
    } else {
      // simple IFSC format: 4 letters + 0 + 6 digits/letters (common), but IFSC formats vary; adjust if needed
      if (!/^[A-Za-z]{4}0[A-Z0-9]{6}$/.test(formData.bankDetails.ifsc.toString().trim())) {
        // Not strictly required; we warn but keep requirement satisfied to avoid blocking if format differs
        // We'll still accept but show helper message (optional). For now, mark invalid:
        newErrors["bankDetails.ifsc"] = "Enter a valid IFSC code (e.g. ABCD0XXXXX)";
      }
    }

    // SWIFT code (optional but validate if provided)
    const swiftVal = (formData.bankDetails.swift || "").toString().trim();
    if (swiftVal) {
      if (!SWIFT_REGEX.test(swiftVal)) {
        newErrors["bankDetails.swift"] = "Enter a valid SWIFT code (8 or 11 uppercase chars)";
      }
    }

    // UPI ID (optional but validate if provided)
    const upiVal = (formData.bankDetails.upiId || "").toString().trim();
    if (upiVal) {
      if (!UPI_REGEX.test(upiVal)) {
        newErrors["bankDetails.upiId"] = "Enter a valid UPI ID (e.g. username@bank)";
      }
    }

    // Preferred mode: now required and must be valid
    const pref = (formData.bankDetails.preferredMode || "").toString().trim();
    if (!pref) {
      newErrors["bankDetails.preferredMode"] = "Preferred payment mode is required";
    } else if (!ALLOWED_PREFERRED_MODES.includes(pref)) {
      newErrors["bankDetails.preferredMode"] = `Preferred payment mode must be one of: ${ALLOWED_PREFERRED_MODES.join(", ")}`;
    }

    // Products: require at least 1
    if (!Array.isArray(formData.products) || formData.products.length === 0) {
      newErrors.products = "Select at least one product";
    }

    // Status
    if (!formData.status) newErrors.status = "Status is required";

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  // Clear specific errors as user corrects fields
  useEffect(() => {
    setErrors((prev) => {
      const next = { ...prev };

      if (next.name && formData.name && formData.name.toString().trim()) delete next.name;
      if (next.contactPerson && formData.contactPerson && formData.contactPerson.toString().trim()) delete next.contactPerson;

      const rawPhone = (formData.phone || "").toString();
      const cleanedPhone = rawPhone.replace(/\D/g, "");
      if (next.phone && cleanedPhone && /^\d{10}$/.test(cleanedPhone)) delete next.phone;

      if (next.email && formData.email && /^\S+@\S+\.\S+$/.test(formData.email)) delete next.email;
      if (next.businessType && formData.businessType) delete next.businessType;
      if (next.panNumber && formData.panNumber && formData.panNumber.toString().trim()) delete next.panNumber;

      if (next["billingAddress.street"] && formData.billingAddress.street && formData.billingAddress.street.toString().trim()) delete next["billingAddress.street"];
      if (next["billingAddress.city"] && formData.billingAddress.city && formData.billingAddress.city.toString().trim()) delete next["billingAddress.city"];
      if (next["billingAddress.state"] && formData.billingAddress.state && formData.billingAddress.state.toString().trim()) delete next["billingAddress.state"];
      if (next["billingAddress.country"] && formData.billingAddress.country && formData.billingAddress.country.toString().trim()) delete next["billingAddress.country"];
      if (next["billingAddress.pincode"]) {
        const p = formData.billingAddress.pincode?.toString()?.trim() || "";
        if (p && /^\d{4,6}$/.test(p)) delete next["billingAddress.pincode"];
      }

      if (next["bankDetails.accountHolder"] && formData.bankDetails.accountHolder && formData.bankDetails.accountHolder.toString().trim()) delete next["bankDetails.accountHolder"];
      if (next["bankDetails.accountNumber"] && formData.bankDetails.accountNumber && formData.bankDetails.accountNumber.toString().trim()) {
        const acct = formData.bankDetails.accountNumber.toString().replace(/\s|-/g, "");
        if (/^\d{6,24}$/.test(acct)) delete next["bankDetails.accountNumber"];
      }
      if (next["bankDetails.ifsc"] && formData.bankDetails.ifsc && /^[A-Za-z]{4}0[A-Z0-9]{6}$/.test(formData.bankDetails.ifsc.toString().trim())) delete next["bankDetails.ifsc"];

      // Swift cleanup
      const swiftVal = (formData.bankDetails.swift || "").toString().trim();
      if (next["bankDetails.swift"] && (!swiftVal || SWIFT_REGEX.test(swiftVal))) delete next["bankDetails.swift"];

      // UPI cleanup
      const upiVal = (formData.bankDetails.upiId || "").toString().trim();
      if (next["bankDetails.upiId"] && (!upiVal || UPI_REGEX.test(upiVal))) delete next["bankDetails.upiId"];

      // preferredMode cleanup if valid
      const pref = (formData.bankDetails.preferredMode || "").toString().trim();
      if (next["bankDetails.preferredMode"] && pref && ALLOWED_PREFERRED_MODES.includes(pref)) delete next["bankDetails.preferredMode"];

      if (next.products && Array.isArray(formData.products) && formData.products.length > 0) delete next.products;

      if (next.status && formData.status) delete next.status;

      return next;
    });
  }, [formData]);

  const handleSubmit = async (e) => {
    // If invoked from button without event, e may be undefined; keep defensive
    if (e && typeof e.preventDefault === "function") e.preventDefault();

    // Validate
    if (!validateForm()) {
      // optionally scroll to top or focus first error
      return;
    }

    try {
      const tokenValue = sessionStorage.getItem("token");
      const token = tokenValue ? `Bearer ${tokenValue}` : "";

      // Build payload
      const payload = {
        ...formData,
        products: selectedProducts.map(p => ({
          productId: p._id,
          moq: Number(p.moq) || 0,
          leadTime: Number(p.leadTime) || 0
        })),
      };

      // IMPORTANT FIX:
      // Ensure preferredMode is removed if empty (server enum) — but we require it now so this is protective
      if (payload.bankDetails && payload.bankDetails.preferredMode !== undefined) {
        const pref = payload.bankDetails.preferredMode;
        if (pref === "" || pref === null) {
          delete payload.bankDetails.preferredMode;
        } else {
          payload.bankDetails.preferredMode = String(pref);
        }
      }

      if (formData._id) {
        await putData(`supplier/update/${formData._id}`, payload, token, true);
      } else {
        await postData("supplier/add", payload, token, true);
      }
      handleCloseForm();
      await fetchData();
    } catch (err) {
      console.error(formData._id ? "Failed to update supplier" : "Failed to add supplier", err);
      // you can surface server-side validation errors here if desired
    }
  };

  // Render error state
  if (error) {
    return (
      <Box sx={{ p: 3, minHeight: "100vh" }}>
        <Alert severity="error" sx={{ mb: 2, ...MulishFont }}>
          {error}
          <Button
            variant="text"
            onClick={fetchData}
            sx={{ ml: 2, color: "#d31919ff", textTransform: "none", ...MulishFont }}
          >
            Try Again
          </Button>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', p: { xs: 1, md: 2 } }}>
      {!showForm && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h5" sx={{ fontFamily: 'Mulish, sans-serif', fontWeight: 800, color: '#111827' }}>
              Supplier Management
            </Typography>
            <Typography sx={{ fontSize: '0.82rem', color: '#6b7280', fontFamily: 'Mulish, sans-serif' }}>
              {pagination.total} total suppliers
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: "12px" }}>
            <Button variant="outlined" startIcon={<FilterIcon />} onClick={() => {
              setTempFilters(filters);
              setFilterDrawerOpen(true);
            }} sx={{
              fontFamily: 'Mulish, sans-serif', textTransform: 'none', fontWeight: 600,
              borderColor: '#e5e7eb', color: '#6b7280', borderRadius: '8px',
              '&:hover': { bgcolor: '#f9fafb', borderColor: '#d1d5db' }
            }}>
              Filters
            </Button>
            <Button
              variant="contained"
              startIcon={<IoPersonAddSharp />}
              onClick={handleToggleView}
              sx={{
                fontFamily: 'Mulish, sans-serif', bgcolor: '#ffd102', color: '#000',
                fontWeight: 700, textTransform: 'none', borderRadius: '10px', px: 3,
                boxShadow: '0 4px 12px rgba(255,209,2,0.3)',
                '&:hover': { bgcolor: '#f59e0b', boxShadow: '0 6px 16px rgba(255,209,2,0.4)' }
              }}
            >
              Add Supplier
            </Button>
          </Box>
        </Box>
      )}

      {showForm ? (
        <Box
          sx={{
            backgroundColor: "white",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            borderRadius: "12px",
            marginTop: "-20px",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography
              variant="h4"
              sx={{
                paddingLeft: "23px",
                paddingTop: "23px",
                ...MulishFont,
              }}
            >
              {formData._id ? "Edit Supplier" : "Add New Supplier"}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCloseForm}
              sx={{
                marginRight: "20px",
                background: "red",
                color: "white",
                fontWeight: 700,
                "&:hover": {
                  background: "red",
                },
              }}
            >
              Back
            </Button>
          </Box>

          <Paper sx={{ p: 3, boxShadow: "none" }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 500, color: "#333", ...MulishFont }}>
              Basic Information
            </Typography>

            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    sx={{ ...MulishFont }}
                    label="Business Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    variant="outlined"
                    size="small"
                    error={!!errors.name}
                    helperText={errors.name}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    sx={{ ...MulishFont }}
                    label="Contact Person"
                    name="contactPerson"
                    value={formData.contactPerson}
                    onChange={handleChange}
                    required
                    variant="outlined"
                    size="small"
                    error={!!errors.contactPerson}
                    helperText={errors.contactPerson}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    sx={{
                      ...MulishFont,
                    }}
                    label="Phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    variant="outlined"
                    size="small"
                    error={!!errors.phone}
                    helperText={errors.phone}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    sx={{ ...MulishFont }}
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    variant="outlined"
                    size="small"
                    error={!!errors.email}
                    helperText={errors.email}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth sx={{ ...MulishFont }} size="small" error={!!errors.businessType}>
                    <InputLabel sx={{ ...MulishFont }}>Business Type</InputLabel>
                    <Select name="businessType" value={formData.businessType} label="Business Type" onChange={handleChange} required sx={{ ...MulishFont }}>
                      <MenuItem value="individual" sx={{ ...MulishFont }}>
                        Individual
                      </MenuItem>
                      <MenuItem value="proprietor" sx={{ ...MulishFont }}>
                        Proprietor
                      </MenuItem>
                      <MenuItem value="company" sx={{ ...MulishFont }}>
                        Company
                      </MenuItem>
                      <MenuItem value="distributor" sx={{ ...MulishFont }}>
                        Distributor
                      </MenuItem>
                    </Select>
                    {errors.businessType && <Typography variant="caption" color="error" sx={{ ml: 1 }}>{errors.businessType}</Typography>}
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    sx={{ ...MulishFont }}
                    label="PAN Number"
                    name="panNumber"
                    value={formData.panNumber}
                    onChange={handleChange}
                    required
                    variant="outlined"
                    size="small"
                    error={!!errors.panNumber}
                    helperText={errors.panNumber}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    sx={{ ...MulishFont }}
                    label="GST Number"
                    name="gstNumber"
                    value={formData.gstNumber}
                    onChange={handleChange}
                    variant="outlined"
                    size="small"
                    error={!!errors.gstNumber}
                    helperText={errors.gstNumber}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    sx={{ ...MulishFont }}
                    label="Trade License"
                    name="tradeLicense"
                    value={formData.tradeLicense}
                    onChange={handleChange}
                    variant="outlined"
                    size="small"
                    error={!!errors.tradeLicense}
                    helperText={errors.tradeLicense}
                  />
                </Grid>
              </Grid>
            </form>
          </Paper>

          <Paper sx={{ p: 3, boxShadow: "none" }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 500, color: "#333", ...MulishFont }}>
              Billing Address
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  sx={{ ...MulishFont }}
                  label="Street Address"
                  name="billingAddress.street"
                  value={formData.billingAddress.street}
                  onChange={handleChange}
                  required
                  variant="outlined"
                  size="small"
                  error={!!errors["billingAddress.street"]}
                  helperText={errors["billingAddress.street"]}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  sx={{ ...MulishFont }}
                  label="City"
                  name="billingAddress.city"
                  value={formData.billingAddress.city}
                  onChange={handleChange}
                  required
                  variant="outlined"
                  size="small"
                  error={!!errors["billingAddress.city"]}
                  helperText={errors["billingAddress.city"]}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  select
                  sx={{ ...MulishFont }}
                  label="State"
                  name="billingAddress.state"
                  value={formData.billingAddress.state}
                  onChange={handleChange}
                  required
                  variant="outlined"
                  size="small"
                  error={!!errors["billingAddress.state"]}
                  helperText={errors["billingAddress.state"]}
                >
                  {indianStates.map((state) => (
                    <MenuItem key={state} value={state}>
                      {state}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  sx={{ ...MulishFont }}
                  label="Pincode"
                  name="billingAddress.pincode"
                  value={formData.billingAddress.pincode}
                  onChange={handleChange}
                  required
                  variant="outlined"
                  size="small"
                  error={!!errors["billingAddress.pincode"]}
                  helperText={errors["billingAddress.pincode"]}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  sx={{ ...MulishFont }}
                  label="Country"
                  name="billingAddress.country"
                  value={formData.billingAddress.country}
                  onChange={handleChange}
                  required
                  variant="outlined"
                  size="small"
                  error={!!errors["billingAddress.country"]}
                  helperText={errors["billingAddress.country"]}
                />
              </Grid>
            </Grid>
          </Paper>

          <Paper sx={{ p: 3, boxShadow: "none" }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 500, color: "#333", ...MulishFont }}>
              Bank Details
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  sx={{ ...MulishFont }}
                  label="Account Holder Name"
                  name="bankDetails.accountHolder"
                  value={formData.bankDetails.accountHolder}
                  onChange={handleChange}
                  required
                  variant="outlined"
                  size="small"
                  error={!!errors["bankDetails.accountHolder"]}
                  helperText={errors["bankDetails.accountHolder"]}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  sx={{ ...MulishFont }}
                  label="Account Number"
                  name="bankDetails.accountNumber"
                  value={formData.bankDetails.accountNumber}
                  onChange={handleChange}
                  required
                  variant="outlined"
                  size="small"
                  error={!!errors["bankDetails.accountNumber"]}
                  helperText={errors["bankDetails.accountNumber"]}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  sx={{ ...MulishFont }}
                  label="IFSC Code"
                  name="bankDetails.ifsc"
                  value={formData.bankDetails.ifsc}
                  onChange={handleChange}
                  required
                  variant="outlined"
                  size="small"
                  error={!!errors["bankDetails.ifsc"]}
                  helperText={errors["bankDetails.ifsc"]}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  sx={{ ...MulishFont }}
                  label="SWIFT Code"
                  name="bankDetails.swift"
                  value={formData.bankDetails.swift}
                  onChange={handleChange}
                  variant="outlined"
                  size="small"
                  error={!!errors["bankDetails.swift"]}
                  helperText={errors["bankDetails.swift"]}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  sx={{ ...MulishFont }}
                  label="UPI ID"
                  name="bankDetails.upiId"
                  value={formData.bankDetails.upiId}
                  onChange={handleChange}
                  variant="outlined"
                  size="small"
                  error={!!errors["bankDetails.upiId"]}
                  helperText={errors["bankDetails.upiId"] || "Format: username@bank"}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth sx={{ ...MulishFont }} size="small" error={!!errors["bankDetails.preferredMode"]}>
                  <InputLabel sx={{ ...MulishFont }}>Preferred Payment Mode</InputLabel>
                  <Select
                    name="bankDetails.preferredMode"
                    value={formData.bankDetails.preferredMode}
                    label="Preferred Payment Mode"
                    onChange={handleChange}
                    sx={{ ...MulishFont }}
                  >
                    <MenuItem value="Bank Transfer" sx={{ ...MulishFont }}>
                      Bank Transfer
                    </MenuItem>
                    <MenuItem value="UPI" sx={{ ...MulishFont }}>
                      UPI
                    </MenuItem>
                    <MenuItem value="Cheque" sx={{ ...MulishFont }}>
                      Cheque
                    </MenuItem>
                    <MenuItem value="Cash" sx={{ ...MulishFont }}>
                      Cash
                    </MenuItem>
                  </Select>
                  {errors["bankDetails.preferredMode"] && (
                    <Typography variant="caption" color="error" sx={{ ml: 1 }}>
                      {errors["bankDetails.preferredMode"]}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
            </Grid>
          </Paper>

          <Paper sx={{ p: 3, boxShadow: "none" }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 500, color: "#333", ...MulishFont }}>
              Products & Additional Information
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  options={productsOptions}
                  value={selectedProducts}
                  disableCloseOnSelect
                  filterSelectedOptions
                  getOptionLabel={(option) => option?.title || option?.id || "Selected"}
                  isOptionEqualToValue={(option, value) => {
                    const optId = option?._id || option?.id;
                    const valId = value?._id || value?.id;
                    return String(optId) === String(valId);
                  }}
                  onChange={(_, value) => {
                    const nextValue = value.map(v => {
                      const existing = selectedProducts.find(p => p._id === v._id);
                      return existing ? existing : { ...v, moq: 0, leadTime: 0 };
                    });
                    setSelectedProducts(nextValue);
                    setFormData((prev) => ({
                      ...prev,
                      products: nextValue.map((v) => ({
                        productId: v._id,
                        moq: v.moq || 0,
                        leadTime: v.leadTime || 0
                      })),
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Add Product"
                      placeholder="Search and select products"
                      variant="outlined"
                      size="small"
                      sx={{ ...MulishFont }}
                      error={!!errors.products}
                    />
                  )}
                  sx={{ ...MulishFont }}
                />
                {errors.products && (
                  <Typography variant="caption" color="error" sx={{ ml: 1 }}>
                    {errors.products}
                  </Typography>
                )}
              </Grid>

              {selectedProducts.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, color: "#555", ...MulishFont }}>
                    Configure Individual Product MOQ
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {selectedProducts.map((product, index) => (
                      <Paper key={product._id || product.id || `product-${index}`} variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#fcfcfc' }}>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} sm={4}>
                            <Typography sx={{ fontWeight: 600, ...MulishFont }}>{product.title}</Typography>
                            <Typography variant="caption" color="textSecondary">SKU: {product.sku || 'N/A'}</Typography>
                          </Grid>
                          <Grid item xs={6} sm={4}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Individual MOQ"
                              type="number"
                              value={product.moq || 0}
                              onChange={(e) => {
                                const newMoq = Number(e.target.value);
                                const updated = [...selectedProducts];
                                updated[index] = { ...updated[index], moq: newMoq };
                                setSelectedProducts(updated);
                                setFormData(prev => ({
                                  ...prev,
                                  products: updated.map(p => ({
                                    productId: p._id,
                                    moq: p.moq || 0,
                                    leadTime: p.leadTime || 0
                                  }))
                                }));
                              }}
                              sx={{ ...MulishFont }}
                            />
                          </Grid>
                          <Grid item xs={6} sm={4}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Lead Time (Days)"
                              type="number"
                              value={product.leadTime || 0}
                              onChange={(e) => {
                                const newLead = Number(e.target.value);
                                const updated = [...selectedProducts];
                                updated[index] = { ...updated[index], leadTime: newLead };
                                setSelectedProducts(updated);
                                setFormData(prev => ({
                                  ...prev,
                                  products: updated.map(p => ({
                                    productId: p._id,
                                    moq: p.moq || 0,
                                    leadTime: p.leadTime || 0
                                  }))
                                }));
                              }}
                              sx={{ ...MulishFont }}
                            />
                          </Grid>
                        </Grid>
                      </Paper>
                    ))}
                  </Box>
                </Grid>
              )}

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth sx={{ ...MulishFont }} size="small" error={!!errors.status}>
                  <InputLabel sx={{ ...MulishFont }}>Status</InputLabel>
                  <Select name="status" value={formData.status} label="Status" onChange={handleChange} required sx={{ ...MulishFont }}>
                    <MenuItem value="active" sx={{ ...MulishFont }}>
                      Active
                    </MenuItem>
                    <MenuItem value="inactive" sx={{ ...MulishFont }}>
                      Inactive
                    </MenuItem>
                  </Select>
                  {errors.status && <Typography variant="caption" color="error" sx={{ ml: 1 }}>{errors.status}</Typography>}
                </FormControl>
              </Grid>
            </Grid>
          </Paper>

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, padding: "20px" }}>
            <Button
              onClick={handleCloseForm}
              sx={{
                color: "#666666",
                textTransform: "none",
                padding: "8px 24px",
                border: "1px solid #ddd",
                ...MulishFont,
                "&:hover": { backgroundColor: "#f5f5f5" },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              sx={{
                textTransform: "none",
                padding: "8px 24px",
                color: "black",
                fontFamily: "Mulish, sans-serif",
                border: "1px solid black",
                background: "rgb(255 209 2)",
                "&:hover": {
                  background: "rgb(255 209 2)",
                },
              }}
            >
              {formData._id ? "Update Supplier" : "Add Supplier"}
            </Button>
          </Box>
        </Box>
      ) : (
        <Box>
          {/* Filter Drawer UI */}
          <Drawer
            anchor="right"
            open={filterDrawerOpen}
            onClose={() => setFilterDrawerOpen(false)}
          >
            <Box sx={{ width: 350, p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="h6" sx={{ mb: 1, fontFamily: 'Mulish, sans-serif', fontWeight: 700 }}>
                Filters
              </Typography>

              <TextField
                select
                fullWidth
                label="Business Type"
                variant="outlined"
                size="small"
                value={tempFilters.businessType}
                onChange={e => setTempFilters({ ...tempFilters, businessType: e.target.value })}
              >
                <MenuItem value="">Any Type</MenuItem>
                <MenuItem value="individual">Individual</MenuItem>
                <MenuItem value="proprietor">Proprietor</MenuItem>
                <MenuItem value="company">Company</MenuItem>
                <MenuItem value="distributor">Distributor</MenuItem>
              </TextField>

              <TextField
                fullWidth
                variant="outlined"
                label="PAN Number"
                size="small"
                value={tempFilters.panNumber}
                onChange={e => setTempFilters({ ...tempFilters, panNumber: e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase() })}
              />

              <TextField
                fullWidth
                variant="outlined"
                label="GST Number"
                size="small"
                value={tempFilters.gstNumber}
                onChange={e => setTempFilters({ ...tempFilters, gstNumber: e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase() })}
              />

              <TextField
                select
                fullWidth
                label="Status"
                variant="outlined"
                size="small"
                value={tempFilters.status}
                onChange={e => setTempFilters({ ...tempFilters, status: e.target.value })}
              >
                <MenuItem value="">Any Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </TextField>

              <TextField
                fullWidth
                variant="outlined"
                label="City"
                size="small"
                value={tempFilters.city}
                onChange={e => setTempFilters({ ...tempFilters, city: e.target.value })}
              />

              <TextField
                select
                fullWidth
                variant="outlined"
                label="State"
                size="small"
                value={tempFilters.state}
                onChange={e => setTempFilters({ ...tempFilters, state: e.target.value })}
              >
                <MenuItem value="">Any State</MenuItem>
                {indianStates.map((state) => (
                  <MenuItem key={state} value={state}>
                    {state}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                fullWidth
                variant="outlined"
                label="Phone"
                size="small"
                value={tempFilters.phone}
                onChange={e => setTempFilters({ ...tempFilters, phone: e.target.value })}
              />

              <TextField
                fullWidth
                variant="outlined"
                label="Contact Person"
                size="small"
                value={tempFilters.contactPerson}
                onChange={e => setTempFilters({ ...tempFilters, contactPerson: e.target.value })}
              />

              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => {
                    const cleared = {
                      businessType: "",
                      panNumber: "",
                      gstNumber: "",
                      status: "",
                      city: "",
                      state: "",
                      phone: "",
                      contactPerson: ""
                    };
                    setTempFilters(cleared);
                    setFilters(cleared);
                    setPagination(prev => ({ ...prev, page: 1 }));
                    setFilterDrawerOpen(false);
                  }}
                  sx={{
                    fontFamily: 'Mulish, sans-serif',
                    borderRadius: '10px',
                    color: '#4b5563',
                    borderColor: '#e5e7eb',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': { borderColor: '#d1d5db', backgroundColor: '#f9fafb' }
                  }}
                >
                  Clear All
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => {
                    setFilters(tempFilters);
                    setPagination(prev => ({ ...prev, page: 1 }));
                    setFilterDrawerOpen(false);
                  }}
                  sx={{
                    fontFamily: 'Mulish, sans-serif',
                    borderRadius: '10px',
                    background: 'rgb(255 209 2)',
                    color: 'black',
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    boxShadow: '0 4px 12px rgba(255, 209, 2, 0.3)',
                    border: '1px solid black',
                    '&:hover': {
                      background: 'rgb(255 209 2)',
                      boxShadow: '0 6px 16px rgba(255, 209, 2, 0.4)',
                      transform: 'translateY(-1px)'
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  Apply
                </Button>
              </Box>
            </Box>
          </Drawer>

          <Box sx={{
            bgcolor: '#fff', borderRadius: 2, border: '1px solid #e5e7eb',
            boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden'
          }}>
            <TableContainer>
              <Table stickyHeader>
                <TableHead>
                  <TableRow
                    sx={{
                      "& th": {
                        backgroundColor: "#fafafa", fontFamily: 'Mulish, sans-serif',
                        fontWeight: 800, fontSize: '0.72rem', textTransform: 'uppercase',
                        letterSpacing: '0.05em', color: '#6b7280',
                        borderBottom: '2px solid #e5e7eb', py: 1.5, whiteSpace: 'nowrap'
                      },
                    }}
                  >
                    <TableCell>Supplier Name</TableCell>
                    <TableCell>Business Details</TableCell>
                    <TableCell>Contact Info</TableCell>
                    <TableCell>Products</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    Array.from({ length: 5 }).map((_, idx) => (
                      <TableRow key={idx}>
                        <TableCell><Skeleton variant="text" width="100%" /></TableCell>
                        <TableCell><Skeleton variant="text" width="100%" /></TableCell>
                        <TableCell><Skeleton variant="text" width="100%" /></TableCell>
                        <TableCell><Skeleton variant="text" width="100%" /></TableCell>
                        <TableCell align="right"><Skeleton variant="circular" width={30} height={30} sx={{ ml: 'auto' }} /></TableCell>
                      </TableRow>
                    ))
                  ) : suppliersData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ py: 6, textAlign: "center" }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                          <img
                            style={{ width: '180px' }}
                            src='/img/nodata.png'
                            alt='no data'
                          />
                          <Typography variant="h6" sx={{ fontFamily: "Mulish, sans-serif", color: "text.secondary" }}>
                            No suppliers found.
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    suppliersData.map((supplier, index) => (
                      <TableRow
                        key={index}
                        onClick={() => handleRowClick(supplier)}
                        sx={{
                          backgroundColor: "#ffffff",
                          cursor: "pointer",
                          "&:hover": {
                            backgroundColor: "#f9fafb",
                          },
                          transition: "background-color 0.2s ease"
                        }}
                      >
                        <TableCell>
                          <Typography
                            fontWeight={700}
                            sx={{
                              fontFamily: "Mulish, sans-serif",
                              fontSize: "0.95rem",
                              color: "#1a1c1e",
                              "&:hover": { color: "#0d47a1" }
                            }}
                          >
                            {supplier?.name || "-"}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ fontFamily: "Mulish, sans-serif", color: "#9ca3af", display: "block" }}
                          >
                            Type: {supplier?.businessType || "N/A"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            fontWeight={500}
                            sx={{ fontFamily: "Mulish, sans-serif", color: "#4b5563", fontSize: "0.85rem", lineHeight: 1.4 }}
                          >
                            {supplier?.billingAddress
                              ? `${supplier.billingAddress.street || ""}${supplier.billingAddress.city ? ", " + supplier.billingAddress.city : ""}${supplier.billingAddress.state ? ", " + supplier.billingAddress.state : ""
                              }${supplier.billingAddress.pincode ? " – " + supplier.billingAddress.pincode : ""}`
                              : "-"}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ fontFamily: "Mulish, sans-serif", color: "#9ca3af" }}
                          >
                            PAN: {supplier?.panNumber || "N/A"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            fontWeight={600}
                            sx={{ fontFamily: "Mulish, sans-serif", color: "#4b5563", fontSize: "0.9rem" }}
                          >
                            {supplier?.phone || "-"}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ fontFamily: "Mulish, sans-serif", color: "#9ca3af", display: "block" }}
                          >
                            {supplier?.email || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: "Mulish, sans-serif",
                              color: "#6b7280",
                              maxWidth: "250px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap"
                            }}
                          >
                            {Array.isArray(supplier?.products) && supplier.products.length > 0 ? supplier.products.map((p) => p.productId?.title || "").filter(Boolean).join(", ") : "-"}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                            <IconButton
                              size="small"
                              sx={{
                                color: "#ca8a04",
                                backgroundColor: "rgba(202, 138, 4, 0.1)",
                                "&:hover": { backgroundColor: "rgba(202, 138, 4, 0.2)" },
                                borderRadius: "8px"
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRowClick(supplier);
                              }}
                            >
                              <MdEdit size={20} />
                            </IconButton>
                            <IconButton
                              size="small"
                              sx={{
                                color: "#dc2626",
                                backgroundColor: "rgba(220, 38, 38, 0.1)",
                                "&:hover": { backgroundColor: "rgba(220, 38, 38, 0.2)" },
                                borderRadius: "8px"
                              }}
                              onClick={(e) => handleDeleteClick(supplier._id, e)}
                            >
                              <MdDeleteForever size={20} />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2, borderTop: '1px solid #e5e7eb' }}>
              <Pagination
                count={pagination.totalPages}
                page={pagination.page}
                onChange={handlePageChange}
                shape="rounded"
                showFirstButton
                showLastButton
                sx={{
                  '& .MuiPaginationItem-root': {
                    fontFamily: 'Mulish, sans-serif',
                    '&.Mui-selected': { bgcolor: '#ffd102', color: '#000' }
                  }
                }}
              />
            </Box>
          </Box>

          {/* Pagination Info - BELOW Pagination */}
          {pagination.total > 0 && (
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1.5, textAlign: "center", fontFamily: "Mulish, sans-serif" }}>
              Showing {(pagination.page - 1) * pagination.limit + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} entries
            </Typography>
          )}
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleDeleteCancel}
        PaperProps={{
          style: {
            borderRadius: "10px",
            padding: "20px",
            minWidth: "400px",
          },
        }}
      >
        {/* Icon / Header */}
        <DialogTitle sx={{ fontSize: "20px", fontWeight: "bold", textAlign: "center", pb: 1 }}>
          <img style={{ width: "80px" }} src="/img/del1.png" alt="delete" />
        </DialogTitle>

        {/* Message and Warning Box */}
        <DialogContent>
          <DialogContentText sx={{ textAlign: "center", fontSize: "16px", mb: 2, fontFamily: "Mulish, sans-serif" }}>
            Are you sure you want to delete <b style={{ color: "#000" }}>{"this Supplier"}</b>?
          </DialogContentText>

          <Box sx={{ textAlign: "center", backgroundColor: "#fff3cd", padding: "12px", borderRadius: "4px", borderLeft: "4px solid #ffc107", mb: 1 }}>
            <Typography variant="body1" sx={{ fontWeight: "bold", color: "#856404", mb: "3px", fontFamily: "Mulish, sans-serif" }}>
              Warning
            </Typography>
            <Typography variant="body2" sx={{ color: "#856404", fontFamily: "Mulish, sans-serif" }}>
              This action is permanent and cannot be undone.
            </Typography>
          </Box>
        </DialogContent>

        {/* Action Buttons */}
        <DialogActions sx={{ justifyContent: "center", gap: "20px", padding: "20px" }}>
          <Button
            onClick={handleDeleteCancel}
            variant="outlined"
            sx={{
              minWidth: "120px",
              borderColor: "gray",
              color: "gray",
              textTransform: "none",
              fontWeight: 500,
              fontFamily: "Mulish, sans-serif",
              "&:hover": {
                borderColor: "darkgray",
                backgroundColor: "rgba(0,0,0,0.05)",
              },
            }}
          >
            No, Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            sx={{
              minWidth: "120px",
              backgroundColor: "red",
              color: "#fff",
              textTransform: "none",
              fontWeight: 500,
              fontFamily: "Mulish, sans-serif",
              "&:hover": {
                backgroundColor: "#bb2d3b",
              },
            }}
            autoFocus
          >
            Yes, Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box >
  );
}

export default SupplierDetails;
